// pages/CourseEnrollPage.jsx
// Course Enrollment Page — implements "Course Enrollment Page.dc.html" from the
// "Video course purchase page" design project. One page for every track,
// switched by route param (the course slug). All course content — metadata,
// about, outcomes, FAQs, curriculum, durations, enrolled counts — comes from
// /api/courses, which carries the catalogue price; the Reviews-tab cards come
// from the workbook APIs
// (same wiring as CoursesPage). Nothing course-specific is hardcoded here.

import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { workbooksApi } from "../api/workbooks";
import { coursesApi } from "../api/courses";
import { startCheckout } from "../api/payment";
import { isLoggedIn } from "../api/auth";
import { loginUrlFor } from "../lib/nextUrl";
import { enrolledFor } from "../data/courseStats";
import Navbar from "../components/Navbar";
import VideoPlayer from "../components/VideoPlayer";
import { EarlyBirdOfferBlock } from "../components/EarlyBirdCountdown";
import { THEME } from "../theme";
import LoadErrorPanel from "../components/LoadErrorPanel";
import { settled, isAnswered, isFailed, LOADING_STATE } from "../lib/fetchState";

const C = {
  cream: "#f2efe8",
  cream2: "#ece9e0",
  ink: "#1c1a17",
  ink2: "#3d3a34",
  muted: "#8a8478",
  border: "rgba(28,26,23,0.11)",
  border2: "rgba(28,26,23,0.22)",
  blue: "#2563eb",
  blueDark: "#1d4ed8",
  gold: "#d97706",
  brand: "#1e1b4b",
  text: "#18181b",
  textMuted: "#71717a",
  textSoft: "#a1a1aa",
  code: "#0d1117",
};

// Header chip colors per course accent (the accent name is data, the palette
// is presentation).
const ACCENT_CHIP = {
  blue: { bg: "#dbeafe", color: "#1d4ed8" },
  red: { bg: "#fee2e2", color: "#991b1b" },
  amber: { bg: "#fef3c7", color: "#92400e" },
  green: { bg: "#dcfce7", color: "#166534" },
};

const LINKEDIN_URL = "https://linkedin.com/in/prasath-r-5584343b7";

// Pre-launch gate for the enroll CTA. False = normal enrollment. The disabled
// button + waitlist form stay in the code, dormant, in case we need them again.
const PRE_LAUNCH = false;

// Open pre-order: checkout is live now, content unlocks on launch day. The
// launch date itself (LAUNCH_AT and the "1st August" copy) lives in
// components/EarlyBirdCountdown.jsx.
const PRE_ORDER = true;

// Static marketing boilerplate (not course data).
const GUARANTEES = [
  "Lifetime access, all future updates",
  "Watch anywhere, any device",
  "7-day refund, no questions asked",
];

const AUTHOR_BIO =
  "I've spent 8+ years building automation tools for design teams - SolidWorks add-ins, AutoCAD .NET plugins, and batch pipelines that save engineers 10+ hours a week. Every lesson in this course comes from tools I've actually shipped.";

const inr = (n) => "₹" + n.toLocaleString("en-IN");
const initialsOf = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";

// "8h 05m" for course totals, "2h 15m" / "45m" for sections, "5m" for lessons.
const fmtTotal = (sec) => {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
};
const fmtCompact = (sec) => {
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
};
const fmtLesson = (sec) => `${Math.max(1, Math.round(sec / 60))}m`;
const fmtClock = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;

const mono = "'Space Mono', monospace";
const jmono = "'JetBrains Mono', monospace";
const jakarta = "'Plus Jakarta Sans', sans-serif";

// Waitlist form tokens: ink text on white/cream fills, 16px rounded, no shadows.
const WL_INK = "#1a1a2e";
const wlInputStyle = {
  fontFamily: jakarta,
  fontSize: 13,
  color: WL_INK,
  background: "#fffdf8",
  border: "1px solid rgba(28,26,23,0.22)",
  borderRadius: 16,
  padding: "9px 14px",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};
const serifTitle = "'Playfair Display', Georgia, serif";
const serifCard = "'Fraunces', Georgia, serif";

export default function CourseEnrollPage() {
  const { trackId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [courses, setCourses] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // How the curriculum read went, apart from what it returned. hasAccess rides
  // on this response, so a failed read used to leave hasAccess false and put a
  // live Enroll button in front of someone who had already bought the course.
  const [curriculumState, setCurriculumState] = useState(LOADING_STATE);
  // The open tab and the expanded curriculum module live in the URL. They were
  // useState, so back from a lesson reset the page to Overview with every module
  // collapsed, and neither could be linked to or survive a refresh.
  const TABS = ["overview", "author", "faq", "reviews"];
  const tabParam = searchParams.get("tab");
  const tab = TABS.includes(tabParam) ? tabParam : "overview";

  // -1 means every module collapsed, which is a state the user can choose, so it
  // has to be distinguishable from "no parameter" (module 0 open).
  const openParam = searchParams.get("open");
  const openSection = (() => {
    if (openParam == null) return 0;
    const n = Number(openParam);
    return Number.isInteger(n) && n >= -1 ? n : 0;
  })();

  // replace on both: a tab and an accordion are views of this page, not places,
  // and pushing put an entry in history for every click.
  const writeParam = (name, value, fallback) => {
    const params = new URLSearchParams(searchParams);
    if (value === fallback) params.delete(name);
    else params.set(name, String(value));
    setSearchParams(params, { replace: true });
  };
  const setTab = (next) => writeParam("tab", next, "overview");
  const setOpenSection = (next) => writeParam("open", next, 0);
  const [previewSrc, setPreviewSrc] = useState(null);
  // Set when the player has given up recovering a failed preview request.
  // Shape is { reason, code } as documented on VideoPlayer.
  const [playbackError, setPlaybackError] = useState(null);
  const [reviews, setReviews] = useState([]);
  // Waitlist form (pre-launch only).
  const [wlName, setWlName] = useState("");
  const [wlEmail, setWlEmail] = useState("");
  const [wlStatus, setWlStatus] = useState("idle"); // idle | submitting | done | error
  const [wlMessage, setWlMessage] = useState("");

  useEffect(() => {
    let mounted = true;
    coursesApi
      .getAll()
      .then((list) => mounted && setCourses(list || []))
      .catch((e) => mounted && setLoadError(e.message));
    Promise.all([
      workbooksApi.getMarquee().catch(() => null),
    ]).then(([rev]) => {
      if (!mounted) return;
      if (rev) {
        const seen = new Set();
        setReviews(
          (rev.reviews ?? []).filter((r) => {
            if (r.id == null) return true;
            if (seen.has(r.id)) return false;
            seen.add(r.id);
            return true;
          }),
        );
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Purchasable tracks drive the switcher, but the viewed course resolves from
  // the full list so a coming-soon course renders its own page instead of
  // falling through to the first track. Unknown slugs still fall back.
  const tracks = (courses || []).filter((c) => !c.comingSoon);
  const course = (courses || []).find((c) => c.slug === trackId) || tracks[0] || null;

  // Reset per-track UI state and load the active course's curriculum.
  useEffect(() => {
    // Tab and open module are not reset here any more: they come from the URL, and
    // a switch between tracks is a navigation that carries its own query string.
    setPreviewSrc(null);
    setPlaybackError(null);
    setCurriculum(null);
    setCurriculumState(LOADING_STATE);
    if (!course) return;
    let mounted = true;
    settled(coursesApi.getCourse(course.slug)).then((result) => {
      if (!mounted) return;
      setCurriculumState(result);
      if (isAnswered(result)) setCurriculum(result.data);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course?.slug]);

  // Access, re-read on demand rather than only at mount. The curriculum response
  // carries hasAccess, so re-reading it is what flips this page to "Continue
  // learning" without a document reload. Same shape as the bundle page's
  // refreshOwnership.
  const courseSlug = course?.slug;
  const refreshAccess = useCallback(() => {
    if (!courseSlug) return Promise.resolve();
    return settled(coursesApi.getCourse(courseSlug)).then((result) => {
      setCurriculumState(result);
      // A failed refresh keeps what is already known rather than downgrading to
      // "no access". It runs straight after a payment, so guessing wrong here is
      // the expensive direction.
      if (!isAnswered(result)) {
        console.error("Access refresh failed:", result.error);
        return;
      }
      if (result.data) setCurriculum(result.data);
    });
  }, [courseSlug]);

  // The webhook-only case: on mobile UPI the buyer leaves for their bank app,
  // the webhook grants while they are away, and the Razorpay handler never
  // runs. Whatever happened, the server knows. Same rule as the bundle page.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshAccess();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refreshAccess]);

  // The curriculum read did not come back, so this page cannot know whether the
  // visitor already owns the course. Nothing that sells, and no price, until it
  // does.
  if (course && isFailed(curriculumState)) {
    return (
      <div style={{ background: C.cream, minHeight: "100vh" }}>
        <Navbar />
        <LoadErrorPanel
          title="We could not load this course"
          message="Something went wrong reaching the server, so we cannot show the curriculum or confirm whether you are already enrolled. Nothing has been charged."
          onRetry={refreshAccess}
          background={C.cream}
          ink={C.text}
          muted={C.muted}
        />
      </div>
    );
  }

  if (loadError || (courses && !course)) {
    return (
      <div style={{ background: C.cream, minHeight: "100vh" }}>
        <Navbar />
        <div className="cep-wrap" style={{ color: C.muted, fontSize: 15 }}>
          {loadError ? `Could not load courses: ${loadError}` : "Course not found."}
        </div>
        <style>{cepCss}</style>
      </div>
    );
  }

  if (!course) {
    return (
      <div style={{ background: C.cream, minHeight: "100vh" }}>
        <Navbar />
        <div className="cep-wrap" style={{ color: C.muted, fontSize: 15 }}>
          Loading course...
        </div>
        <style>{cepCss}</style>
      </div>
    );
  }

  const chip = ACCENT_CHIP[course.accent] || ACCENT_CHIP.blue;
  const about = course.about || [];
  const outcomes = course.outcomes || [];
  const faqs = course.faqs || [];
  const foundation = (courses || []).find((c) => c.product === "csharp");

  // Course-native enrollment count from /api/courses (CourseAccess rows), plus
  // the C# baseline. A bare real count is weak social proof below 10, so other
  // tracks stay hidden until then; the baselined C# number always shows.
  const enrolledTotal = enrolledFor(course);
  const enrolled =
    course.product === "csharp" || enrolledTotal >= 10 ? enrolledTotal : null;

  // The course fee comes from the catalogue: /api/courses returns the price of
  // the active product that sells this course, in paise. There is no workbook
  // fallback any more, which is what used to let this page show the workbook
  // price for a course, and no hardcoded default: a course with no active
  // product shows no price and offers no buy button.
  const price = course.price ? course.price / 100 : null;
  const original = course.originalPrice ? course.originalPrice / 100 : null;
  const discountPct =
    price != null && original != null && original > price
      ? Math.round((1 - price / original) * 100)
      : null;

  // The API only reports a preview episode once its lesson has a real recording,
  // so a course whose videos are still placeholders — coming-soon or not — keeps
  // the header player on the "Preview coming soon" empty state.
  const preview = course.previewEpisode;
  // Thumbnail behind the pre-play overlay; null falls back to the current
  // gradient treatment.
  const previewThumb = preview?.thumbnailUrl || null;

  // Plays the free preview inline: fetch the episode (free ⇒ videoUrl is
  // present) and swap the placeholder for a real player.
  const onPlayPreview = () => {
    if (!preview) return;
    setPlaybackError(null);
    coursesApi
      .getEpisode(course.slug, preview.slug)
      .then((ep) => ep?.videoUrl && setPreviewSrc({ id: ep.id, src: ep.videoUrl, captionsUrl: ep.captionsUrl, poster: ep.thumbnailUrl }))
      .catch(() => {});
  };

  // Re-reads the preview episode purely to get a fresh video URL, for the
  // player's recovery path. Deliberately not coursesApi.getEpisode: apiClient
  // turns a 401 into a hard redirect to /login, which would throw a visitor
  // off the page mid preview. A raw fetch keeps that decision here, where a
  // 401 becomes a message instead of a navigation.
  //
  // It never calls setPreviewSrc: that would hand VideoPlayer a new src prop,
  // and since the src is only read when the effect builds Plyr, the only way
  // it would take is a remount, which discards the position recovery just
  // restored. The player swaps the URL onto its own media element instead.
  // The identifiers this needs are course.slug and preview.slug rather than
  // anything held in previewSrc, so the state shape never gets in the way.
  const refreshPreviewSrc = () => {
    if (!preview) return Promise.resolve(null);
    const headers = {};
    const token = localStorage.getItem("sc_token");
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(
      `${process.env.REACT_APP_API_URL}/api/courses/${course.slug}/episodes/${preview.slug}`,
      { headers },
    )
      .then((res) => {
        if (res.status === 401) {
          const err = new Error("Session expired.");
          err.reason = "unauthorized";
          throw err;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => data?.videoUrl || null);
  };

  const handlePlaybackFailure = (info) => {
    setPlaybackError(info);
  };

  // Razorpay checkout for this course's product. Login is required to create
  // the order, so logged-out buyers go to /login and come straight back.
  const onEnroll = () => {
    if (!isLoggedIn()) {
      // A click, not a state-driven redirect, so this pushes: back should
      // return to the page they chose to leave. ?next= brings them back after
      // signing in, and survives a refresh of /login on the way.
      navigate(loginUrlFor(`/courses/${course.slug}`));
      return;
    }
    startCheckout({
      productCode: course.product,
      title: course.title,
      // Re-read access first, then move into the player, in the SPA. This used
      // to navigate with window.location so the destination refetched on mount;
      // the refresh is explicit now and the reload is gone.
      redirectTo: `/course/${course.slug}`,
      navigate,
      onSettled: refreshAccess,
    });
  };

  // Pre-launch waitlist signup. Duplicate joins come back as a 200 with its
  // own message, so success handling covers both cases.
  const onJoinWaitlist = async () => {
    const name = wlName.trim();
    const email = wlEmail.trim();
    if (!name || !email || !email.includes("@")) {
      setWlStatus("error");
      setWlMessage("Please enter your name and a valid email.");
      return;
    }
    setWlStatus("submitting");
    setWlMessage("");
    try {
      const res = await coursesApi.joinWaitlist({
        name,
        email,
        courseSlug: course.slug,
        source: "enroll-page",
      });
      setWlStatus("done");
      setWlMessage(res?.message || "You are on the list.");
    } catch (e) {
      setWlStatus("error");
      setWlMessage(e.message || "Could not join the waitlist. Please try again.");
    }
  };

  const modules = curriculum?.modules || [];

  // Access is authoritative from the course API (curriculum), NOT the
  // bundleStats `enrolled` count above. Owners get "Continue learning"
  // straight to the resume route and never see checkout.
  const hasAccess = curriculum?.hasAccess === true;
  const onContinue = () => navigate(`/course/${course.slug}`);

  const metaSep = <span style={{ color: C.muted }}>·</span>;

  return (
    <div style={{ background: C.cream, minHeight: "100vh" }}>
      <Navbar />
      <div className="cep-wrap">

        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: mono, fontSize: 11, color: C.muted, marginBottom: 20 }}>
          <Link to="/courses" style={{ color: C.muted, textDecoration: "none" }}>Courses</Link>
          <span>/</span>
          <span>{course.category}</span>
          <span>/</span>
          <span style={{ color: C.ink }}>{course.title}</span>
        </div>

        {/* Track switcher. It only lists purchasable tracks, so a coming-soon
            course is absent from it and would render a lone pill pointing at
            an unrelated course. Hide the whole row in that case. */}
        {tracks.some((t) => t.slug === course.slug) && (
          <div style={{ display: "flex", gap: 8, marginBottom: 28, flexWrap: "wrap" }}>
            {tracks.map((t) => {
              const active = t.slug === course.slug;
              return (
                <button
                  key={t.slug}
                  className="cep-pill"
                  onClick={() => navigate(`/courses/${t.slug}`)}
                  style={{
                    border: `0.5px solid ${active ? C.ink : C.border2}`,
                    background: active ? C.ink : "transparent",
                    color: active ? C.cream : C.ink2,
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Course header */}
        <div className="cep-header" style={{ borderTop: `2px solid ${C.ink}` }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 10, flexWrap: "wrap" }}>
              <h1 style={{ margin: 0, fontFamily: serifTitle, fontSize: 42, fontWeight: 700, lineHeight: 1.05, color: C.ink }}>
                {course.title}
              </h1>
              {course.badge && (
                <span style={{ fontFamily: jmono, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", background: chip.bg, color: chip.color, padding: "5px 12px", borderRadius: 100 }}>
                  {course.badge}
                </span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 18, fontFamily: mono, fontSize: 12, color: C.muted, flexWrap: "wrap" }}>
              <span>▶ {course.lessonCount} lessons</span>
              {course.exerciseCount > 0 && (
                <>
                  {metaSep}
                  <span>✎ {course.exerciseCount} exercises</span>
                </>
              )}
              {metaSep}
              <span>{fmtTotal(course.durationSeconds)} of video</span>
              {enrolled != null && (
                <>
                  {metaSep}
                  <span>{enrolled} enrolled</span>
                </>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 20, flexShrink: 0, paddingTop: 6 }}>
            {(course.product !== "csharp" || hasAccess) && price != null && (
              <div style={{ textAlign: "right" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, justifyContent: "flex-end" }}>
                  <span style={{ fontFamily: serifCard, fontSize: 28, fontWeight: 700, color: C.ink }}>{inr(price)}</span>
                  {original != null && original > price && (
                    <span style={{ fontSize: 14, color: C.muted, textDecoration: "line-through" }}>{inr(original)}</span>
                  )}
                  {discountPct != null && (
                    <span style={{ fontFamily: jmono, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", background: "#dcfce7", color: "#166534", padding: "3px 8px", borderRadius: 100 }}>
                      {discountPct}% off
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: C.muted }}>One-time · lifetime access</div>
              </div>
            )}
            {hasAccess ? (
              <button className="cep-enroll" onClick={onContinue}>
                Continue learning →
              </button>
            ) : course.product === "csharp" ? (
              /* C# uses the full-width early-bird band below the header; the
                 header right side stays empty for it. */
              null
            ) : course.comingSoon ? (
              /* Announced but not purchasable: the curriculum is real, checkout
                 is not. Static note, no click target, no checkout. */
              <span
                className="cep-enroll"
                style={{ display: "inline-block", background: C.textSoft, opacity: 0.75, cursor: "default" }}
              >
                Coming soon
              </span>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 7 }}>
                <button
                  className="cep-enroll"
                  onClick={onEnroll}
                  disabled={PRE_LAUNCH}
                  style={PRE_LAUNCH ? { background: C.textSoft, opacity: 0.75, cursor: "not-allowed" } : undefined}
                >
                  Enroll now →
                </button>
                {PRE_LAUNCH && (
                  <span style={{ fontFamily: jakarta, fontSize: 12, fontWeight: 500, color: C.textMuted }}>
                    Launching 24th July
                  </span>
                )}
                {PRE_LAUNCH &&
                  (wlStatus === "done" ? (
                    <div style={{ fontFamily: jakarta, fontSize: 12.5, color: WL_INK, opacity: 0.75, maxWidth: 232, textAlign: "center" }}>
                      {wlMessage}
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: 232, marginTop: 4 }}>
                      <span style={{ fontFamily: jakarta, fontSize: 12, fontWeight: 600, color: WL_INK }}>
                        Join the waitlist
                      </span>
                      <input
                        type="text"
                        placeholder="Your name"
                        value={wlName}
                        onChange={(e) => setWlName(e.target.value)}
                        style={wlInputStyle}
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={wlEmail}
                        onChange={(e) => setWlEmail(e.target.value)}
                        style={wlInputStyle}
                      />
                      <button
                        onClick={onJoinWaitlist}
                        disabled={wlStatus === "submitting"}
                        style={{
                          fontFamily: jakarta,
                          fontSize: 13,
                          fontWeight: 600,
                          background: C.blue,
                          color: "#fff",
                          border: "none",
                          borderRadius: 16,
                          padding: "10px 14px",
                          cursor: wlStatus === "submitting" ? "default" : "pointer",
                          opacity: wlStatus === "submitting" ? 0.7 : 1,
                        }}
                      >
                        {wlStatus === "submitting" ? "Joining..." : "Join waitlist"}
                      </button>
                      {wlStatus === "error" && (
                        <span style={{ fontFamily: jakarta, fontSize: 11.5, color: "#b91c1c" }}>
                          {wlMessage}
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* Early-bird offer band: full-width horizontal row with framing,
            countdown tiles, and the price + checkout unit. Post-launch it
            slims to just price + Enroll now. */}
        {!hasAccess && course.product === "csharp" && (
          <EarlyBirdOfferBlock showFraming={PRE_ORDER} style={{ marginBottom: 28 }}>
            {price != null && (
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontFamily: THEME.fonts.price, fontSize: 28, fontWeight: 700, color: THEME.ink }}>{inr(price)}</span>
                {original != null && original > price && (
                  <span style={{ fontFamily: THEME.fonts.body, fontSize: 14, color: THEME.grey, textDecoration: "line-through" }}>{inr(original)}</span>
                )}
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {discountPct != null && (
                <span style={{ fontFamily: THEME.fonts.body, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", background: THEME.green, color: "#fff", padding: "3px 10px", borderRadius: 999 }}>
                  {discountPct}% off
                </span>
              )}
              <span style={{ fontFamily: THEME.fonts.body, fontSize: 12, color: THEME.grey }}>One-time · lifetime access</span>
            </div>
            <button className="cep-enroll" onClick={onEnroll} style={{ width: "100%", borderRadius: 12 }}>
              Enroll now →
            </button>
          </EarlyBirdOfferBlock>
        )}

        {/* Two-column body */}
        <div className="cep-body">

          {/* Left column */}
          <div style={{ display: "flex", flexDirection: "column", gap: 24, minWidth: 0 }}>

            {/* Preview player — the placeholder swaps for a real inline
                player when the visitor hits play. */}
            <div style={{ background: C.code, borderRadius: 8, overflow: "hidden", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", position: "relative" }}>
              {previewSrc ? (
                <VideoPlayer
                  episodeId={previewSrc.id}
                  src={previewSrc.src}
                  poster={previewSrc.poster}
                  captionsUrl={previewSrc.captionsUrl}
                  autoPlay
                  refreshSrc={refreshPreviewSrc}
                  onUnrecoverableError={handlePlaybackFailure}
                />
              ) : (
                <>
                  <div
                    style={{
                      aspectRatio: "16 / 9", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20, position: "relative",
                      ...(previewThumb && { backgroundImage: `url(${previewThumb})`, backgroundSize: "cover", backgroundPosition: "center" }),
                    }}
                  >
                    {/* Dark tint keeps the chip/title legible over a real image. */}
                    <div style={{ position: "absolute", inset: 0, background: previewThumb ? "rgba(13,17,23,0.6)" : "radial-gradient(ellipse at 50% 40%, rgba(37,99,235,0.22), transparent 65%)" }} />
                    {!previewThumb && <span style={{ position: "absolute", top: -60, left: -60, width: 240, height: 240, borderRadius: "50%", background: C.blue, opacity: 0.07 }} />}
                    {!previewThumb && <span style={{ position: "absolute", bottom: -80, right: -40, width: 300, height: 300, borderRadius: "50%", background: C.blue, opacity: 0.08 }} />}
                    <span style={{ position: "absolute", top: 18, left: 20, fontFamily: jmono, fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", border: "0.5px solid rgba(255,255,255,0.18)", padding: "4px 12px", borderRadius: 100 }}>
                      Free preview
                    </span>
                    <button className="cep-play" onClick={onPlayPreview} aria-label="Play free preview">
                      <span style={{ width: 0, height: 0, borderTop: "13px solid transparent", borderBottom: "13px solid transparent", borderLeft: "21px solid #fff", marginLeft: 6 }} />
                    </button>
                    <div style={{ position: "relative", textAlign: "center", padding: "0 16px" }}>
                      <div style={{ fontFamily: serifCard, fontSize: 20, fontWeight: 700, color: "#fff", marginBottom: 4 }}>
                        {preview ? preview.title : course.title}
                      </div>
                      <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: "0.08em", color: "rgba(255,255,255,0.5)" }}>
                        {preview ? `Lesson 01 · ${fmtClock(preview.durationSeconds)}` : "Preview coming soon"}
                      </div>
                    </div>
                  </div>
                  {preview && (
                    <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 18px", background: "rgba(255,255,255,0.04)", borderTop: "0.5px solid rgba(255,255,255,0.08)" }}>
                      <span style={{ color: "#fff", fontSize: 12 }}>▶</span>
                      <div style={{ flex: 1, height: 3, background: "rgba(255,255,255,0.15)", borderRadius: 100, overflow: "hidden" }}>
                        <span style={{ display: "block", width: "18%", height: "100%", background: C.blue }} />
                      </div>
                      <span style={{ fontFamily: jmono, fontSize: 10, color: "rgba(255,255,255,0.45)" }}>0:00 / {fmtClock(preview.durationSeconds)}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* The player above stays mounted through a failure: unmounting it
                would throw away the position it recovered to. */}
            {playbackError && (
              <div style={{ background: "#fef2f2", border: "1px solid rgba(225,29,72,0.2)", borderLeft: "3px solid #e11d48", borderRadius: 8, padding: "16px 20px" }}>
                <div style={{ fontFamily: jmono, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#e11d48", marginBottom: 6 }}>
                  Playback stopped
                </div>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 300, lineHeight: 1.7, color: C.ink2 }}>
                  {playbackError.reason === "unauthorized" ? (
                    <>
                      Your session expired.{" "}
                      <Link to="/login" style={{ color: C.blueDark, fontWeight: 600 }}>
                        Sign in
                      </Link>{" "}
                      to continue watching.
                    </>
                  ) : (
                    "This preview stopped playing and could not be reloaded. Reload the page to try again."
                  )}
                </p>
              </div>
            )}

            {/* Content tabs */}
            <div style={{ display: "flex", gap: 6, borderBottom: `0.5px solid ${C.border}` }}>
              {[["overview", "Overview"], ["author", "Author"], ["faq", "FAQ"], ["reviews", "Reviews"]].map(([id, label]) => {
                const active = tab === id;
                return (
                  <button
                    key={id}
                    className="cep-tab"
                    onClick={() => setTab(id)}
                    style={{
                      borderBottom: `2px solid ${active ? C.blue : "transparent"}`,
                      color: active ? C.ink : C.muted,
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Tab: Overview */}
            {tab === "overview" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                {about.length > 0 && (
                  <div className="cep-panel">
                    <h2 style={{ margin: "0 0 16px", fontFamily: serifCard, fontSize: 24, fontWeight: 700, color: C.ink }}>About this course</h2>
                    {about.map((p, i) => (
                      <p key={i} style={{ margin: i < about.length - 1 ? "0 0 14px" : 0, fontSize: 15, fontWeight: 300, lineHeight: 1.8, color: C.ink2 }}>
                        {p}
                      </p>
                    ))}
                  </div>
                )}
                {outcomes.length > 0 && (
                  <div className="cep-panel">
                    <h2 style={{ margin: "0 0 20px", fontFamily: serifCard, fontSize: 24, fontWeight: 700, color: C.ink }}>What you'll learn</h2>
                    <div className="cep-outcomes">
                      {outcomes.map((o, i) => (
                        <div key={i} style={{ display: "flex", gap: 10, fontSize: 14, lineHeight: 1.6, color: C.ink2 }}>
                          <span style={{ color: C.blue, flexShrink: 0 }}>→</span>
                          <span>{o}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {course.includesFoundation && (
                  <div style={{ background: "#dbeafe", border: "0.5px solid rgba(37,99,235,0.25)", borderRadius: 8, padding: "20px 26px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 14, lineHeight: 1.6, color: C.blueDark }}>
                      <span style={{ fontWeight: 600 }}>✓ C# Foundation included free.</span>{" "}
                      This track bundles the full C# Foundation video course - start from zero code experience.
                    </div>
                    {foundation && (
                      <Link to={`/courses/${foundation.slug}`} style={{ fontFamily: mono, fontSize: 11, letterSpacing: "0.04em", whiteSpace: "nowrap", color: C.blue, textDecoration: "none" }}>
                        See what's inside →
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Tab: Author */}
            {tab === "author" && (
              <div className="cep-panel">
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 18 }}>
                  <span style={{ width: 56, height: 56, borderRadius: "50%", background: C.brand, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: mono, fontSize: 18, fontWeight: 700, flexShrink: 0 }}>PR</span>
                  <div>
                    <div style={{ fontFamily: serifCard, fontSize: 21, fontWeight: 700, color: C.ink }}>Prasath R</div>
                    <div style={{ fontFamily: mono, fontSize: 11, color: C.muted }}>CAD Automation Engineer · 8+ years · Chennai</div>
                  </div>
                </div>
                <p style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 300, lineHeight: 1.8, color: C.ink2 }}>{AUTHOR_BIO}</p>
                <a href={LINKEDIN_URL} target="_blank" rel="noreferrer" style={{ fontFamily: mono, fontSize: 12, letterSpacing: "0.04em", color: C.blue, textDecoration: "none" }}>
                  Connect on LinkedIn →
                </a>
              </div>
            )}

            {/* Tab: FAQ */}
            {tab === "faq" && (
              <div className="cep-panel" style={{ padding: "12px 36px" }}>
                {faqs.length === 0 && (
                  <div style={{ padding: "20px 0", fontSize: 14, color: C.textMuted }}>No FAQs yet.</div>
                )}
                {faqs.map((f, i) => (
                  <div key={i} style={{ padding: "20px 0", borderBottom: i < faqs.length - 1 ? `0.5px solid ${C.border}` : "none" }}>
                    <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: C.ink }}>{f.q}</div>
                    <div style={{ fontSize: 14, fontWeight: 300, lineHeight: 1.7, color: C.ink2 }}>{f.a}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Tab: Reviews */}
            {tab === "reviews" && (
              <div className="cep-reviews">
                {reviews.length === 0 && (
                  <div style={{ fontSize: 14, color: C.textMuted }}>No reviews yet.</div>
                )}
                {reviews.map((r, i) => (
                  <div key={r.id ?? i} style={{ background: "#fff", border: `0.5px solid ${C.border}`, borderRadius: 8, padding: "22px 24px" }}>
                    <div style={{ fontSize: 15, letterSpacing: 2, marginBottom: 10 }}>
                      <span style={{ color: C.gold }}>{"★".repeat(r.rating)}</span>
                      <span style={{ color: "#d4d4d8" }}>{"★".repeat(5 - r.rating)}</span>
                    </div>
                    {r.text && r.text.trim() !== "" ? (
                      <div style={{ fontSize: 14.5, color: C.text, marginBottom: 18 }}>{r.text}</div>
                    ) : (
                      <div style={{ fontSize: 14.5, fontStyle: "italic", color: C.textMuted, marginBottom: 18 }}>Rated {r.rating} of 5</div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ width: 36, height: 36, borderRadius: "50%", background: C.brand, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                        {initialsOf(r.name)}
                      </span>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{r.name}</div>
                        {r.role && <div style={{ fontSize: 12, color: C.textMuted }}>{r.role}</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>

          {/* Right column */}
          <div className="cep-side">

            {/* Course content accordion */}
            <div style={{ background: "#fff", border: `0.5px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
              <div style={{ padding: "20px 24px 16px", display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                <h3 style={{ margin: 0, fontFamily: serifCard, fontSize: 18, fontWeight: 700, color: C.ink }}>Course content</h3>
                <span style={{ fontFamily: mono, fontSize: 10.5, color: C.muted }}>
                  {course.lessonCount} lessons · {fmtTotal(course.durationSeconds)}
                </span>
              </div>
              {modules.length === 0 && (
                <div style={{ padding: "16px 24px", borderTop: `0.5px solid ${C.border}`, fontSize: 13, color: C.textMuted }}>
                  {curriculum ? "Curriculum coming soon." : "Loading curriculum..."}
                </div>
              )}
              {modules.map((s, i) => {
                const open = openSection === i;
                const sectionSeconds = (s.episodes || []).reduce((n, e) => n + (e.durationSeconds || 0), 0);
                return (
                  <div key={s.slug ?? i} style={{ borderTop: `0.5px solid ${C.border}` }}>
                    <button
                      className="cep-acc"
                      onClick={() => setOpenSection(open ? -1 : i)}
                      style={{ background: open ? C.cream2 : "#fff" }}
                    >
                      <span style={{ fontFamily: jmono, fontSize: 11, fontWeight: 700, color: C.muted }}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 600, color: C.text, flex: 1 }}>{s.title}</span>
                      <span style={{ fontFamily: mono, fontSize: 10.5, color: C.muted }}>{fmtCompact(sectionSeconds)}</span>
                      <span style={{ fontSize: 11, color: C.muted, transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s" }}>▾</span>
                    </button>
                    {open && (
                      <div style={{ padding: "4px 24px 14px" }}>
                        {(s.episodes || []).map((l, j) => {
                          const isEx = l.kind === "exercise";
                          // Free lessons open straight in the player; the rest
                          // are watchable only after enrolling. Coming soon
                          // does not change that: the lock is enforced by the
                          // backend, which returns videoUrl null for every
                          // locked episode whatever the client links to.
                          const watchable = l.isFree;
                          return (
                            <div
                              key={l.slug ?? j}
                              className={watchable ? "cep-lesson-free" : undefined}
                              onClick={
                                watchable
                                  ? () => navigate(`/courses/${course.slug}/${l.slug}`)
                                  : undefined
                              }
                              role={watchable ? "link" : undefined}
                              style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderBottom: "0.5px solid rgba(28,26,23,0.06)", cursor: watchable ? "pointer" : "default" }}
                            >
                              <span style={{ fontSize: 10, color: isEx ? "#e11d48" : C.muted }}>{isEx ? "✎" : "▶"}</span>
                              <span style={{ fontSize: 13.5, fontWeight: 300, color: watchable ? C.blue : C.ink2, flex: 1 }}>{l.title}</span>
                              {l.isFree && (
                                <span style={{ fontFamily: jmono, fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase", background: "#dbeafe", color: C.blueDark, padding: "2px 8px", borderRadius: 100 }}>
                                  Free
                                </span>
                              )}
                              <span style={{ fontFamily: mono, fontSize: 10.5, color: C.textSoft }}>
                                {isEx ? `~${fmtLesson(l.durationSeconds)}` : fmtLesson(l.durationSeconds)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Author mini card */}
            <div style={{ background: "#fff", border: `0.5px solid ${C.border}`, borderRadius: 8, padding: "22px 24px" }}>
              <h3 style={{ margin: "0 0 14px", fontFamily: serifCard, fontSize: 18, fontWeight: 700, color: C.ink }}>Author</h3>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <span style={{ width: 42, height: 42, borderRadius: "50%", background: C.brand, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: mono, fontSize: 14, fontWeight: 700, flexShrink: 0 }}>PR</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: C.ink }}>Prasath R</div>
                  <div style={{ fontSize: 12, color: C.textMuted }}>CAD Automation Engineer</div>
                </div>
              </div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 300, lineHeight: 1.65, color: C.textMuted }}>
                8+ years automating SolidWorks and AutoCAD. Every lesson comes from tools actually shipped to design teams.
              </p>
            </div>

            {/* Guarantee strip */}
            <div style={{ border: `0.5px solid ${C.border2}`, borderRadius: 8, padding: "16px 22px", display: "flex", flexDirection: "column", gap: 8 }}>
              {GUARANTEES.map((g, i) => (
                <div key={i} style={{ display: "flex", gap: 10, fontSize: 13, color: C.ink2 }}>
                  <span style={{ color: C.blue }}>✓</span> {g}
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>

      <style>{cepCss}</style>
    </div>
  );
}

const cepCss = `
  .cep-wrap{max-width:1300px;margin:0 auto;padding:36px 40px 80px;font-family:'DM Sans',system-ui,sans-serif;}
  .cep-pill{font-family:'Space Mono',monospace;font-size:11px;letter-spacing:0.04em;padding:8px 16px;border-radius:100px;cursor:pointer;transition:opacity .2s;}
  .cep-pill:hover{opacity:0.85;}
  .cep-header{padding-top:24px;display:flex;justify-content:space-between;align-items:flex-start;gap:40px;margin-bottom:32px;flex-wrap:wrap;}
  .cep-enroll{font-family:'Space Mono',monospace;font-size:12px;letter-spacing:0.04em;background:#2563eb;color:#fff;border:none;padding:13px 26px;border-radius:3px;cursor:pointer;transition:opacity .2s;white-space:nowrap;}
  .cep-enroll:hover{opacity:0.85;}
  .cep-body{display:grid;grid-template-columns:1fr 380px;gap:28px;align-items:start;}
  .cep-side{display:flex;flex-direction:column;gap:20px;position:sticky;top:76px;}
  .cep-play{position:relative;width:78px;height:78px;border-radius:50%;background:#2563eb;border:none;color:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:opacity .2s;}
  .cep-play:hover{opacity:0.85;}
  .cep-tab{font-family:'Space Mono',monospace;font-size:11.5px;letter-spacing:0.03em;padding:10px 16px 12px;background:none;border:none;cursor:pointer;transition:color .2s;}
  .cep-tab:hover{color:#1c1a17;}
  .cep-panel{background:#fff;border:0.5px solid rgba(28,26,23,0.11);border-radius:8px;padding:32px 36px;}
  .cep-outcomes{display:grid;grid-template-columns:1fr 1fr;gap:12px 32px;}
  .cep-reviews{display:grid;grid-template-columns:1fr 1fr;gap:16px;}
  .cep-acc{width:100%;display:flex;align-items:center;gap:12px;padding:15px 24px;border:none;cursor:pointer;text-align:left;font-family:'DM Sans',sans-serif;transition:background .15s;}
  .cep-acc:hover{background:#ece9e0 !important;}
  .cep-lesson-free:hover span:nth-child(2){text-decoration:underline;}
  @media (max-width:1000px){
    .cep-body{grid-template-columns:1fr;}
    .cep-side{position:static;}
  }
  @media (max-width:760px){
    .cep-wrap{padding:28px 20px 56px;}
    .cep-panel{padding:24px 20px;}
    .cep-outcomes{grid-template-columns:1fr;}
    .cep-reviews{grid-template-columns:1fr;}
  }
`;
